import { useEffect, useRef, useState } from 'react'

import { api } from 'app/api'
import {
  GET_VENDING_MACHINE_CHANGES,
  convertParams,
  convertResult,
} from 'app/common/api/requests/data/vending/machines/changes/get'
import type {
  MachineChange,
  MachineChangeAction,
  MachineChanges,
} from 'app/common/types/vending/machines/changes'
import HistoryModal from 'app/common/ui-components/history-modal'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import { PlainItemsSelector } from 'app/common/ui-next/plain-selector'
import PlainPaging from 'app/common/ui-next/plain-paging'
import { OpenFilterButton } from 'app/common/ui-next/plain-button'
import ControlsBar from 'app/common/ui-next/controls-bar'
import FiltersDropDownLayout, { Column } from 'app/common/layouts/FiltersDropDownLayout'

import styles from './MachineHistory.module.scss'

const PER_PAGE = 50
const columns = ['created', 'source', 'actor', 'description']
const columnNames = ['Дата изменения', 'Место', 'Кто внес изменения', 'Что было изменено']
const operations = ['Создан', 'Изменен', 'Удален', 'Восстановлен'].map((title, index) => ({
  id: String(index + 1),
  title,
}))
const defaultOperationSelection: Array<string> = []

export const MachineHistory = ({ machineId, title }: { machineId: number; title: string }) => {
  const [displayed, setDisplayed] = useState(false)
  const [page, setPage] = useState(0)
  const [actionId, setActionId] = useState(0)
  const [draftActionId, setDraftActionId] = useState(0)
  const [filtersDisplayed, setFiltersDisplayed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<Array<MachineChange>>([])
  const [count, setCount] = useState(0)
  const requestVersion = useRef(0)

  useEffect(() => {
    if (!displayed || !machineId) return
    const version = ++requestVersion.current
    let active = true
    const request = async () => {
      setLoading(true)
      const result: MachineChanges | null = await api.request(GET_VENDING_MACHINE_CHANGES, {
        error: 'Не удалось загрузить историю изменений аппарата',
        params: {
          limit: PER_PAGE,
          offset: page * PER_PAGE,
          machineId,
          actionId: actionId ? (actionId as MachineChangeAction) : undefined,
        },
        convertions: { convertParams, convertResult },
      })
      if (!active || version !== requestVersion.current) return
      setItems(result?.items || [])
      setCount(result?.count || 0)
      setLoading(false)
    }
    request()
    return () => {
      active = false
    }
  }, [actionId, displayed, machineId, page])

  return (
    <HistoryModal
      className={styles.modal}
      title={`История изменений — ${title}`}
      loading={loading}
      columnIdsList={columns}
      columnNamesList={columnNames}
      columnPropsList={columns.map(() => ({ alignLeft: true }))}
      itemsList={items}
      renderCellContent={(columnId, item) =>
        columnId === 'description' ? (
          <span className={styles.multiline}>{item.description}</span>
        ) : (
          item[columnId]
        )
      }
      mainContentMinHeight="40vh"
      mainContentMaxHeight="40vh"
      onDisplayStatusChange={setDisplayed}
      headerContentClassName={styles.filtersContent}
      headerContent={
        <>
          <ControlsBar className={styles.filtersToggle}>
            <OpenFilterButton
              checked={Boolean(actionId)}
              opened={filtersDisplayed}
              disabled={loading}
              onClick={() => {
                setDraftActionId(actionId)
                setFiltersDisplayed((current) => !current)
              }}
            />
          </ControlsBar>
          {filtersDisplayed && (
            <FiltersDropDownLayout
              className={styles.filtersPanel}
              contentClassName={styles.filtersContentInner}
              loading={loading}
              wide
              wideOrientation
              onClose={() => setFiltersDisplayed(false)}
              onApplyFilter={() => {
                setActionId(draftActionId)
                setPage(0)
              }}
              onResetAll={() => {
                setDraftActionId(0)
                setActionId(0)
                setPage(0)
              }}
            >
              <Column>
                <PlainItemsSelector
                  id="machine-history-action"
                  label="Тип изменения"
                  header="Выберите тип изменения"
                  classNameLabel={styles.filterLabel}
                  items={operations}
                  selection={draftActionId ? [String(draftActionId)] : []}
                  defaultSelection={defaultOperationSelection}
                  onChange={([id]) => setDraftActionId(Number(id) || 0)}
                  colorInverse
                  disableAutoSelect
                  multiple={false}
                  skipUpperSpace
                  disabled={loading}
                />
              </Column>
            </FiltersDropDownLayout>
          )}
        </>
      }
      footerContent={
        count > 0 ? (
          <PlainPaging
            total={count}
            perPage={PER_PAGE}
            currentPageIndex={page}
            currentPageSize={items.length}
            onPage={setPage}
          />
        ) : undefined
      }
    >
      {(toggle) => (
        <PlainButtonAsLink onClick={toggle} disabled={!machineId}>
          {'История изменений'}
        </PlainButtonAsLink>
      )}
    </HistoryModal>
  )
}
